import { useEffect, useState } from 'react';

import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FormProvider, useForm } from 'react-hook-form';
import { ArrowLeft } from 'lucide-react';
import { PAGES } from '../../../constants/pages';
import { apolloClient } from '../../../App';

import { BoxState, DeliveryFragmentFragment, useGetDeliveryQuery, useUpdateDeliveryMutation } from 'data/__generated__';
import { DELIVERIES_UPDATED_SUBSCRIPTION } from 'data/subscriptions/delivery';
import { deliveryUpdatedHandler } from '../utils/deliverySubscriptionsHandlers';

import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { DriverSection } from './DriverSection';
import { GeneralInfoSection } from './GeneralInfoSection';
import { StockTransfersSection } from './StockTransfersSection';
import { OrdersSection } from './OrdersSection';
import { PhotosSectionContent } from './PhotosSectionContent';
import { PhotosSection as SiteSetupPhotoSection } from '../../SiteSetups/details/PhotosSection';
import { StatesSection } from './StatesSection';
import { DeliveryFormData } from '../constants/types';
import { UpdateStockConfirmationModal } from 'components/UpdateStockConfirmationModal';
import { SectionContainer } from 'components/DetailsView/Section';
import { BoxesContentForm } from 'pages/Bundles/components/BoxesContentForm';
import { toast } from 'react-toastify';

type ParamTypes = {
    deliveryId: string;
};

export const DeliveryDetails = () => {
    const { deliveryId = '' } = useParams<ParamTypes>();
    const methods = useForm<DeliveryFormData>();
    const {
        formState: { isDirty },
        reset,
    } = methods;
    const [updateDelivery, { loading: updateDeliveryLoading }] = useUpdateDeliveryMutation();
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

    const {
        loading: deliveryLoading,
        data: deliveryData,
        error: deliveryError,
    } = useGetDeliveryQuery({
        variables: { deliveryId },
    });

    useEffect(() => {
        if (deliveryData?.delivery) {
            methods.reset(deliveryData.delivery);
        }
    }, [deliveryData, methods]);

    // Dépendance volontairement limitée à `deliveryId` : une dépendance sur la livraison chargée
    // recréait la souscription à chaque écriture dans le cache (donc à chaque évènement reçu), ce qui
    // multipliait les souscriptions ouvertes et les évènements reçus en double.
    useEffect(() => {
        const observer = apolloClient.subscribe({
            query: DELIVERIES_UPDATED_SUBSCRIPTION,
        });

        const subscription = observer.subscribe(({ data }) => {
            if (!data) {
                return;
            }
            const { deliveriesUpdated } = data as { deliveriesUpdated: DeliveryFragmentFragment[] };
            deliveryUpdatedHandler(deliveryId, deliveriesUpdated);
        });

        return () => subscription.unsubscribe();
    }, [deliveryId]);

    if (deliveryLoading && !deliveryData) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (deliveryError || !deliveryData) {
        throw new Error('Une erreur est survenue lors de la récupération de la livraison');
    }

    const { delivery } = deliveryData;

    if (!delivery) {
        return (
            <Container>
                <Header>
                    <HeaderTitle>
                        <PageTitle page={PAGES.deliveryDetails} />
                    </HeaderTitle>
                    <CTAsContainer>
                        <Link to={PAGES.deliveries.url}>
                            <TotemPrimaryButton $isSecondaryStyle>Retour</TotemPrimaryButton>
                        </Link>
                    </CTAsContainer>
                </Header>
                <div style={{ color: 'red', textAlign: 'center', marginTop: 50 }}>Livraison introuvable</div>
            </Container>
        );
    }

    const hasQuantityPickedChanges = (): boolean => {
        if (!deliveryData?.delivery) return false;

        const initialBoxes = deliveryData.delivery.boxes || [];
        const formBoxes = methods.getValues().boxes || [];

        // Fonction pour créer une map avec boxId-productId comme clé et quantityPicked comme valeur (en excluant 0 et null)
        const getQuantityPickedMap = (
            boxes: Array<{
                _id: string;
                products?: Array<{ _id: string; quantityPicked?: number | null }> | null;
            }>,
        ): Map<string, number> => {
            const quantityMap = new Map<string, number>();

            for (const box of boxes) {
                for (const product of box.products || []) {
                    const quantityPicked = product.quantityPicked;
                    // Exclure null, undefined et 0
                    if (quantityPicked !== null && quantityPicked !== undefined && quantityPicked !== 0) {
                        const key = `${box._id}-${product._id}`;
                        quantityMap.set(key, quantityPicked);
                    }
                }
            }

            return quantityMap;
        };

        // Boxes staged for removal (state Aborted) or bundle detachment (bundleId nulled) get their
        // quantities reset server-side → treat them as removed so the stock-update modal triggers.
        const initialBundleIdByBoxId = new Map(initialBoxes.map((box) => [box._id, box.bundleId]));
        const activeFormBoxes = formBoxes.filter(
            (box) =>
                box.state !== BoxState.Aborted &&
                !(box.bundleId == null && initialBundleIdByBoxId.get(box._id) != null),
        );

        const initialMap = getQuantityPickedMap(initialBoxes);
        const formMap = getQuantityPickedMap(activeFormBoxes);

        // Étape 1 : Comparer les clés avec un Set
        const initialKeys = new Set(initialMap.keys());
        const formKeys = new Set(formMap.keys());

        // Vérifier si les sets de clés sont différents
        if (initialKeys.size !== formKeys.size || ![...initialKeys].every((value) => formKeys.has(value))) {
            return true;
        }

        // Étape 2 : Comparer les valeurs clé par clé
        for (const [key, initialValue] of initialMap.entries()) {
            const formValue = formMap.get(key);
            if (formValue === undefined || initialValue !== formValue) {
                return true;
            }
        }

        return false;
    };

    const handleUpdateButton = async () => {
        if (hasQuantityPickedChanges()) {
            setIsConfirmationModalOpen(true);
        } else {
            // Aucune modification de quantityPicked, appeler directement avec shouldUpdateStocks=false
            await handleConfirm(false);
        }
    };

    const handleConfirm = async (shouldUpdateStocks: boolean) => {
        const deliveryFormData = methods.getValues();

        try {
            // bundleId/state carry the staged actions: the API detaches the box from its bundle when
            // bundleId turns null, and removes it from the delivery when state turns Aborted.
            const formattedBoxes = deliveryFormData.boxes.map((box) => ({
                _id: box._id,
                code: box.code,
                bundleId: box.bundleId ?? null,
                state: box.state,
                products: box.products.map((product) => ({
                    _id: product._id,
                    quantity: Number(product.quantity),
                    quantityPicked: product.quantityPicked !== null ? Number(product.quantityPicked) : null,
                    unitPrice: product.unitPrice !== null ? Number(product.unitPrice) : null,
                })),
            }));

            const { data } = await updateDelivery({
                variables: {
                    delivery: {
                        _id: deliveryId,
                        state: deliveryFormData.state,
                        boxes: formattedBoxes,
                    },
                    shouldUpdateStocks,
                },
            });

            if (data?.updateDelivery.delivery) {
                toast.success('La livraison a bien été mise à jour');
                reset(data.updateDelivery.delivery);
            }
        } catch (error) {
            toast.error('Une erreur est survenue lors de la mise à jour de la livraison');
        }

        setIsConfirmationModalOpen(false);
    };

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: PAGES.deliveries.url }],
        },
    ];

    return (
        <Container>
            <FormProvider {...methods}>
                <ContentWrapper>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.deliveryDetails} />
                        </HeaderTitle>
                        <ActionsAndSave
                            actionGroups={actionGroups}
                            onSave={handleUpdateButton}
                            saveDisabled={!isDirty}
                            saveLoading={updateDeliveryLoading}
                            saveTitle="Mettre à jour"
                        />
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <GeneralInfoSection delivery={delivery} />
                            <StockTransfersSection delivery={delivery} />
                            <OrdersSection delivery={delivery} />
                            <StatesSection delivery={delivery} />
                            <DriverSection delivery={delivery} />
                            {delivery.siteSetup ? (
                                <SiteSetupPhotoSection siteSetup={delivery.siteSetup} />
                            ) : (
                                <SectionContainer title="Photos" isInitiallyOpen>
                                    <PhotosSectionContent delivery={delivery} />
                                </SectionContainer>
                            )}
                            <BoxesContentForm filterBy="sequence" />
                        </ScrollableContent>
                    </Content>
                </ContentWrapper>
            </FormProvider>

            <UpdateStockConfirmationModal
                isOpen={isConfirmationModalOpen}
                onClose={() => {
                    setIsConfirmationModalOpen(false);
                }}
                onConfirm={(shouldUpdateStocks) => handleConfirm(shouldUpdateStocks)}
                isLoading={updateDeliveryLoading}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const CTAsContainer = styled.div`
    & > :not(:first-child) {
        margin-left: 5px;
    }
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;
